import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useIsAdmin } from "../hooks/useIsAdmin";
import {
  getEmailLogRuns,
  getEmailLogManualSends,
  getEmailHtml,
  SchedulerRunEntry,
  EmailLogEntry,
  EmailHtmlResponse,
} from "../utils/PowerBIApiCall";
import { isDemoMode } from "../utils/PowerBIApiCall";
import { Loading } from "../ui-components/Loading";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type HistoryTab = "scheduled" | "manual";

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(startTime: string, endTime: string): string {
  const diffMs = new Date(endTime).getTime() - new Date(startTime).getTime();
  if (diffMs < 1000) return "<1s";
  if (diffMs < 60000) return `${Math.round(diffMs / 1000)}s`;
  const mins = Math.floor(diffMs / 60000);
  const secs = Math.round((diffMs % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function formatDurationMs(ms?: number): string {
  if (ms == null) return "\u2014";
  if (ms < 1000) return "<1s";
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    success: "bg-green-50 text-green-700",
    completed: "bg-green-50 text-green-700",
    failed: "bg-red-50 text-red-700",
    skipped: "bg-gray-100 text-gray-500",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] ?? "bg-gray-100 text-gray-600"}`}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Email HTML Preview Modal
// ---------------------------------------------------------------------------

function EmailHtmlModal({
  emailId,
  subject,
  onClose,
}: {
  emailId: string;
  subject: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<EmailHtmlResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const resp = await getEmailHtml(emailId);
        if (!cancelled) setData(resp);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load email");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [emailId]);

  // Write HTML into iframe
  useEffect(() => {
    if (data?.html && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(data.html);
        doc.close();
      }
    }
  }, [data]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Sent Email</h2>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{subject}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 ml-4"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-wmp-500 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-500">Loading email...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-sm text-red-600 font-medium">
                  Failed to load email
                </p>
                <p className="text-xs text-gray-500 mt-1">{error}</p>
              </div>
            </div>
          )}

          {data && !loading && (
            <div
              className="bg-white rounded-lg shadow-sm overflow-hidden mx-auto"
              style={{ maxWidth: 960 }}
            >
              <iframe
                ref={iframeRef}
                title="Sent Email"
                className="w-full border-0"
                style={{ minHeight: 600, height: "100%" }}
                sandbox="allow-same-origin"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <p className="text-xs text-gray-400">
            This is the exact email that was sent to recipients.
          </p>
          <button
            onClick={onClose}
            className="text-sm px-4 py-1.5 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Run detail panel (expanded row)
// ---------------------------------------------------------------------------

function RunDetail({
  run,
  onViewEmail,
}: {
  run: SchedulerRunEntry;
  onViewEmail: (emailId: string, subject: string) => void;
}) {
  if (run.emails.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic py-2 px-4">
        No emails in this run.
      </p>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {run.emails.map((email) => (
        <div
          key={email.id}
          className="flex items-center justify-between px-4 py-3 hover:bg-gray-50/50"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {statusBadge(email.status)}
              <span className="text-sm font-medium text-gray-800 truncate">
                {email.portcoName}
              </span>
              {email.durationMs != null && (
                <span className="text-xs text-gray-400">
                  {formatDurationMs(email.durationMs)}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {email.subject}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              To: {email.recipients.join(", ")}
            </p>
            {email.error && (
              <p className="text-xs text-red-500 mt-0.5">{email.error}</p>
            )}
            {email.skipReason && (
              <p className="text-xs text-gray-400 mt-0.5 italic">
                {email.skipReason}
              </p>
            )}
          </div>
          {email.status === "success" && (
            <button
              onClick={() => onViewEmail(email.id, email.subject)}
              className="ml-4 shrink-0 text-xs font-medium text-wmp-500 hover:text-wmp-600 hover:underline"
            >
              View Email
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main content
// ---------------------------------------------------------------------------

function EmailHistoryContent() {
  const [tab, setTab] = useState<HistoryTab>("scheduled");
  const [runs, setRuns] = useState<SchedulerRunEntry[]>([]);
  const [manualSends, setManualSends] = useState<EmailLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Expanded run IDs
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());

  // Email preview modal state
  const [previewEmailId, setPreviewEmailId] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState("");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [runsData, manualData] = await Promise.all([
        getEmailLogRuns(100),
        getEmailLogManualSends(100),
      ]);
      setRuns(runsData);
      setManualSends(manualData);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load email history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleRun = (runId: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const handleViewEmail = (emailId: string, subject: string) => {
    setPreviewEmailId(emailId);
    setPreviewSubject(subject);
  };

  // Summary stats
  const totalEmails = runs.reduce((sum, r) => sum + r.emailCount, 0);
  const totalErrors = runs.reduce((sum, r) => sum + r.errorCount, 0);
  const lastRun = runs[0];

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link
              to="/admin"
              className="text-sm text-gray-400 hover:text-gray-600"
            >
              Admin
            </Link>
            <span className="text-gray-300">/</span>
            <h1 className="text-xl font-bold text-gray-900">Email History</h1>
          </div>
          <p className="text-sm text-gray-500">
            View past email executions and preview the exact emails that were
            sent.
          </p>
        </div>
        <button
          onClick={loadData}
          className="text-sm px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors font-medium"
        >
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Scheduler Runs
          </p>
          <p className="text-xl font-bold text-gray-900 mt-1">{runs.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Total Emails
          </p>
          <p className="text-xl font-bold text-gray-900 mt-1">{totalEmails}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Errors
          </p>
          <p
            className={`text-xl font-bold mt-1 ${totalErrors > 0 ? "text-red-600" : "text-gray-900"}`}
          >
            {totalErrors}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Last Run
          </p>
          <p className="text-sm font-medium text-gray-900 mt-1">
            {lastRun ? timeAgo(lastRun.startTime) : "\u2014"}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4 w-fit">
        {(["scheduled", "manual"] as HistoryTab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === t
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t === "scheduled" ? "Scheduled Runs" : "Manual Sends"}
            <span className="ml-1.5 text-xs text-gray-400">
              ({t === "scheduled" ? runs.length : manualSends.length})
            </span>
          </button>
        ))}
      </div>

      {/* Scheduled Runs */}
      {tab === "scheduled" && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {runs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">
              No scheduled runs yet.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {runs.map((run) => {
                const isExpanded = expandedRuns.has(run.id);
                return (
                  <div key={run.id}>
                    <button
                      onClick={() => toggleRun(run.id)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <svg
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                        {statusBadge(run.status)}
                        <span className="text-sm text-gray-700">
                          {formatDateTime(run.startTime)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDuration(run.startTime, run.endTime)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-4">
                        <span className="text-xs text-gray-500">
                          {run.emailCount} email
                          {run.emailCount !== 1 ? "s" : ""}
                        </span>
                        {run.errorCount > 0 && (
                          <span className="text-xs text-red-500">
                            {run.errorCount} error
                            {run.errorCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="bg-gray-50/50 border-t border-gray-100">
                        {run.skipReason && (
                          <p className="text-xs text-gray-400 italic px-4 py-2">
                            {run.skipReason}
                          </p>
                        )}
                        <RunDetail run={run} onViewEmail={handleViewEmail} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Manual Sends */}
      {tab === "manual" && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {manualSends.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">
              No manual sends yet.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {manualSends.map((email) => (
                <div
                  key={email.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {statusBadge(email.status)}
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {email.portcoName}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDateTime(email.timestamp)}
                      </span>
                      {email.durationMs != null && (
                        <span className="text-xs text-gray-400">
                          ({formatDurationMs(email.durationMs)})
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {email.subject}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      To: {email.recipients.join(", ")}
                    </p>
                    {email.error && (
                      <p className="text-xs text-red-500 mt-0.5">
                        {email.error}
                      </p>
                    )}
                  </div>
                  {email.status === "success" && (
                    <button
                      onClick={() => handleViewEmail(email.id, email.subject)}
                      className="ml-4 shrink-0 text-xs font-medium text-wmp-500 hover:text-wmp-600 hover:underline"
                    >
                      View Email
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Email preview modal */}
      {previewEmailId && (
        <EmailHtmlModal
          emailId={previewEmailId}
          subject={previewSubject}
          onClose={() => setPreviewEmailId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function EmailHistory() {
  const { isAdmin, loading } = useIsAdmin();

  if (isDemoMode()) {
    return <EmailHistoryContent />;
  }

  if (loading) return <Loading />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <EmailHistoryContent />;
}
